/**
 * Канонические ключи месяцев фискального года (Jul → Jun).
 *
 * UI рендерит названия через i18n (`idea_calc.months.<key>`), а в payload
 * мы пишем именно эти стабильные ключи — независимо от текущей локали.
 * Это нужно для XLSX-экспорта на бэкенде и для безопасного шеринга:
 * прежняя реализация писала `monthName: 'Июль'` независимо от языка
 * пользователя, что ломало en/uk-локали.
 */
export const FISCAL_MONTH_KEYS = [
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
] as const;

export type FiscalMonthKey = typeof FISCAL_MONTH_KEYS[number];

/** Полный i18n путь для каждого фискального месяца. */
export const FISCAL_MONTH_I18N_KEYS: readonly string[] = FISCAL_MONTH_KEYS.map(
  (k) => `idea_calc.months.${k}`
);

/**
 * Получить канонический ключ месяца по индексу 1..12 (фискальный порядок Jul=1).
 * Гарантирует, что в payload всегда уходит стабильный английский ключ,
 * который бэкенд может локализовать при экспорте.
 */
export function fiscalMonthKey(fiscalIndex: number): FiscalMonthKey {
  return FISCAL_MONTH_KEYS[clampFiscalIndex(fiscalIndex) - 1];
}

/** Фискальный год начинается с июля: календарный индекс июля (0 = январь). */
const FISCAL_YEAR_START_CALENDAR_INDEX = 6;

/**
 * Календарный индекс месяца (0 = январь … 11 = декабрь) по фискальному номеру 1..12
 * (1 = июль). Нужен там, где ряд приходит календарным — сезонность Keepa с бэкенда
 * (`calculate_seasonality_from_bsr` раскладывает точки по `datetime.month`), — а
 * таблица плана продаж фискальная: без этого перевода январское отклонение вставало
 * в строку «Июль».
 */
export function calendarMonthIndex(fiscalIndex: number): number {
  return (clampFiscalIndex(fiscalIndex) - 1 + FISCAL_YEAR_START_CALENDAR_INDEX) % 12;
}

/** Защита от мусора в сохранённых данных — номер обрезается в 1..12. */
function clampFiscalIndex(fiscalIndex: number): number {
  return Math.max(1, Math.min(12, Math.floor(fiscalIndex)));
}
